<script setup lang="ts">
    import { ref, onMounted } from 'vue';
    import { gsap } from 'gsap';

    const animateObject1 = ref();
    const animateObject2 = ref();
    const animateObject3 = ref();

    onMounted(() => {
        const animatedObjects = [animateObject1.value, animateObject2.value, animateObject3.value];

        window.addEventListener('mousemove', (e) => {
            const mouseOriginX = e.clientX - window.innerWidth / 2;
            const mouseOriginY = e.clientY - window.innerHeight / 2;
            const mouseDamping = 0.07;

            gsap.to(animatedObjects, {
                x: mouseOriginX * mouseDamping,
                y: mouseOriginY * mouseDamping,
                stagger: 0.1,
                duration: 0.1,
                ease: 'none'
            });
        });
    });
</script>

<template>
    <div class="background">
        <div class="white-gradient"></div>

        <svg
            aria-hidden="true"
            class="mobile"
            viewBox="0 0 430 932"
        >
            <path
                d="M-406.568 289.895-116.673 0l296.452 296.454c156.503 156.503 156.193 410.555-.692 567.44l-289.894 289.894-296.454-296.453c-156.503-156.503-156.19-410.555.693-567.44Z"
                style="fill: url(#m-a); fill-rule: nonzero"
            />
            <path
                d="m-314.86-76.453 857.574-8.863 8.12 785.792c4.285 414.836-368.468 755.013-832.572 759.809l-857.581 8.86-8.112-785.79c-4.29-414.834 368.467-755.011 832.571-759.808Z"
                style="fill: url(#m-b); fill-rule: nonzero"
            />
            <path
                d="m623.269 321.139 496.664 133.08-121.941 455.089c-64.375 240.251-334.455 376.628-603.242 304.606l-496.657-133.079L20.034 625.746c64.375-240.25 334.455-376.627 603.235-304.607Z"
                style="fill: url(#m-c); fill-rule: nonzero"
            />

            <defs>
                <linearGradient
                    id="m-a"
                    x1="0"
                    x2="1"
                    y1="0"
                    y2="0"
                    gradientTransform="rotate(-87.258 458.22 585.363) scale(656.2651)"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop
                        offset="0"
                        style="stop-color: #ff9800; stop-opacity: 0.7"
                    />
                    <stop
                        offset="0"
                        style="stop-color: #ff9800; stop-opacity: 0.7"
                    />
                    <stop
                        offset=".95"
                        style="stop-color: #2594a8; stop-opacity: 0.7"
                    />
                    <stop
                        offset="1"
                        style="stop-color: #2594a8; stop-opacity: 0.7"
                    />
                </linearGradient>
                <linearGradient
                    id="m-b"
                    x1="0"
                    x2="1"
                    y1="0"
                    y2="0"
                    gradientTransform="rotate(-47.241 1503.585 1418.563) scale(2005.53)"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop
                        offset="0"
                        style="
                            stop-color: var(--hero-accent-1, #ff9800);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.65);
                        "
                    />
                    <stop
                        offset=".05"
                        style="
                            stop-color: var(--hero-accent-1, #ff9800);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.65);
                        "
                    />
                    <stop
                        offset=".95"
                        style="
                            stop-color: var(--hero-accent-2, #2594a8);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.65);
                        "
                    />
                    <stop
                        offset="1"
                        style="
                            stop-color: var(--hero-accent-2, #2594a8);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.65);
                        "
                    />
                </linearGradient>
                <linearGradient
                    id="m-c"
                    x1="0"
                    x2="1"
                    y1="0"
                    y2="0"
                    gradientTransform="rotate(-21.064 2624.683 435.71) scale(763.397)"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop
                        offset="0"
                        style="
                            stop-color: var(--hero-accent-1, #ff9800);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.7);
                        "
                    />
                    <stop
                        offset=".05"
                        style="
                            stop-color: var(--hero-accent-1, #ff9800);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.7);
                        "
                    />
                    <stop
                        offset=".95"
                        style="
                            stop-color: var(--hero-accent-2, #2594a8);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.7);
                        "
                    />
                    <stop
                        offset="1"
                        style="
                            stop-color: var(--hero-accent-2, #2594a8);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.7);
                        "
                    />
                </linearGradient>
            </defs>
        </svg>

        <svg
            aria-hidden="true"
            class="desktop"
            viewBox="0 0 1728 958"
        >
            <path
                ref="animateObject1"
                d="m-73.486-191.676 593.931-593.931 607.368 607.37c320.64 320.641 320.005 841.137-1.419 1162.56l-593.929 593.928-607.371-607.367c-320.64-320.641-320-841.138 1.42-1162.56Z"
                style="fill: url(#d-a); fill-rule: nonzero"
            />
            <path
                ref="animateObject2"
                d="M1098.83-46h802.98v735.765c0 388.425-352.279 703.305-786.84 703.305H312V657.305C312 268.88 664.28-46 1098.83-46Z"
                style="fill: url(#d-b); fill-rule: nonzero"
            />
            <path
                ref="animateObject3"
                d="m1527.87 368.291 855.313-62.86 57.598 783.72c30.406 413.742-320.181 776.722-783.061 810.741L802.4 1962.75l-57.59-783.719c-30.41-413.74 320.18-776.72 783.06-810.74Z"
                style="fill: url(#d-c); fill-rule: nonzero"
            />

            <defs>
                <linearGradient
                    id="d-a"
                    x1="0"
                    x2="1"
                    y1="0"
                    y2="0"
                    gradientTransform="scale(1344.5489) rotate(-87.258 .674 .304)"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop
                        offset="0"
                        style="
                            stop-color: var(--hero-accent-1, #ff9800);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.65);
                        "
                    />
                    <stop
                        offset=".05"
                        style="
                            stop-color: var(--hero-accent-1, #ff9800);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.65);
                        "
                    />
                    <stop
                        offset=".95"
                        style="
                            stop-color: var(--hero-accent-2, #2594a8);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.65);
                        "
                    />
                    <stop
                        offset="1"
                        style="
                            stop-color: var(--hero-accent-2, #2594a8);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.65);
                        "
                    />
                </linearGradient>
                <linearGradient
                    id="d-b"
                    x1="0"
                    x2="1"
                    y1="0"
                    y2="0"
                    gradientTransform="scale(1192.167) rotate(-36.064 1.71 -.085)"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop
                        offset="0"
                        style="
                            stop-color: var(--hero-accent-1, #ff9800);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.7);
                        "
                    />
                    <stop
                        offset=".05"
                        style="
                            stop-color: var(--hero-accent-1, #ff9800);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.7);
                        "
                    />
                    <stop
                        offset=".95"
                        style="
                            stop-color: var(--hero-accent-2, #2594a8);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.7);
                        "
                    />
                    <stop
                        offset="1"
                        style="
                            stop-color: var(--hero-accent-2, #2594a8);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.7);
                        "
                    />
                </linearGradient>
                <linearGradient
                    id="d-c"
                    x1="0"
                    x2="1"
                    y1="0"
                    y2="0"
                    gradientTransform="rotate(-47.241 2727.797 16.136) scale(2005.53)"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop
                        offset="0"
                        style="
                            stop-color: var(--hero-accent-1, #ff9800);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.7);
                        "
                    />
                    <stop
                        offset=".05"
                        style="
                            stop-color: var(--hero-accent-1, #ff9800);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.7);
                        "
                    />
                    <stop
                        offset=".95"
                        style="
                            stop-color: var(--hero-accent-2, #2594a8);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.7);
                        "
                    />
                    <stop
                        offset="1"
                        style="
                            stop-color: var(--hero-accent-2, #2594a8);
                            stop-opacity: var(--hero-grad-opacity-strong, 0.7);
                        "
                    />
                </linearGradient>
            </defs>
        </svg>
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/hero-section/hero-background.scss';
</style>
